import { memo, useCallback, useMemo, useState } from 'react';
import { MenuItem, SearchInput } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** One checkable entry; kept separate so toggling re-renders only the rows that changed. */
const ChecklistItem = memo(function ChecklistItem({ item, selected, onToggle }) {
  const handleClick = useCallback(() => onToggle(item.id), [onToggle, item.id]);
  return (
    <MenuItem
      label={item.name}
      description={item.subtitle}
      subtextMenu={Boolean(item.subtitle)}
      checkbox
      selected={selected}
      selectedSurface={false}
      size="small"
      onClick={handleClick}
    />
  );
});

/**
 * Searchable list of roles or groups with checkboxes (the role's studios as subtitle). Used
 * by the `+ Role` popover and the Assign access modal.
 *
 * @param {object} props
 * @param {Array<{ id: string, name: string, subtitle?: string }>} props.items
 * @param {string[]} props.selectedIds
 * @param {(id: string) => void} props.onToggle
 * @param {string} props.searchLabel
 * @param {import('react').ReactNode} [props.summary] Line between the search and the list (e.g. "2 of 5 selected").
 * @param {boolean} [props.showCount] Shows "N available" (what the search leaves) under the search.
 */
function CatalogChecklist({ items, selectedIds, onToggle, searchLabel, summary = null, showCount = false }) {
  const { t } = useTranslation('people-access');
  const [query, setQuery] = useState('');
  const handleChange = useCallback((event) => setQuery(event.target.value), []);
  const handleClear = useCallback(() => setQuery(''), []);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const shown = useMemo(
    () =>
      items.filter((item) =>
        normalize(`${item.name} ${item.subtitle ?? ''}`).includes(normalize(query).trim()),
      ),
    [items, query],
  );

  return (
    <div className="flex flex-col gap-2">
      <SearchInput
        value={query}
        onChange={handleChange}
        onClear={handleClear}
        placeholder={searchLabel}
        aria-label={searchLabel}
        className="w-full"
      />
      {summary}
      {showCount ? (
        <p className="text-xs text-content-muted">{t('import.review.catalog.available', { count: shown.length })}</p>
      ) : null}
      <div role="group" aria-label={searchLabel} className="max-h-48 overflow-y-auto scrollbar-aimsos-auto-sm">
        {shown.length === 0 ? (
          <p className="px-2 py-3 text-sm text-content-muted">{t('import.review.popover.noResults')}</p>
        ) : (
          shown.map((item) => (
            <ChecklistItem key={item.id} item={item} selected={selected.has(item.id)} onToggle={onToggle} />
          ))
        )}
      </div>
    </div>
  );
}

export default memo(CatalogChecklist);
